import { ReactNode } from "react";

import {
  ResolvedCUEAnnotation,
  ResolvedCUEElement,
  ResolvedFieldValue,
} from "../../elements";
import {
  CUEParagraphAnnotationType,
  defaultAnnotationComponents,
  DefaultDebug,
} from "./AnnotationItem";
import { breakAndReplace } from "./utils";

type AnnotationProps = {
  element: ResolvedCUEElement | ResolvedFieldValue;
};

const AnnotationOrder = [
  CUEParagraphAnnotationType.ExternalLink as string,
  CUEParagraphAnnotationType.InternalLink as string,
  CUEParagraphAnnotationType.ImageExternalLink as string,
  CUEParagraphAnnotationType.ImageInternalLink as string,
];

const AnnotationWrapper = ({
  annotations,
  children,
}: {
  annotations: ResolvedCUEAnnotation[];
  children: ReactNode;
}) => {
  return annotations.reduce((children, node) => {
    const AnnotationComponent =
      defaultAnnotationComponents?.[node.name as CUEParagraphAnnotationType] ??
      DefaultDebug;

    const props =
      AnnotationComponent === DefaultDebug
        ? {
            "data-annotationname": node.name,
            "data-annotationvalue": JSON.stringify(node.value),
          }
        : typeof AnnotationComponent === "string"
          ? {}
          : {
              element: node.value,
            };

    return <AnnotationComponent {...props}>{children}</AnnotationComponent>;
  }, children);
};

const isEqualArray = (a: unknown[], b: unknown[]) =>
  a.length === b.length && a.every((d, index) => b.indexOf(d) === index);

export function Annotation({ element }: AnnotationProps): ReactNode {
  const plainText = element.value || "";

  //Need to pass it to breakAndReplace as some text contains only \n that needs to format with <br>
  if (!element.annotations?.length) return breakAndReplace(plainText);

  // support multibyte characters correctly
  let characterArray: string[];
  if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
    // works for modifier like skin tones
    const segmenter = new Intl.Segmenter(); // default to granularity grapheme
    characterArray = [...segmenter.segment(plainText)].map((s) => s.segment);
  } else {
    // fallback, will not work for modifier chars
    // eslint-disable-next-line @typescript-eslint/no-misused-spread
    characterArray = [...plainText];
  }

  const orderedAnnotations = [...element.annotations].sort(
    (a, b) =>
      // sort to enusure extenal_link and internal_link are applied first
      AnnotationOrder.indexOf(a.name) - AnnotationOrder.indexOf(b.name) ||
      // stable order for all types
      (a.name > b.name ? -1 : a.name < b.name ? 1 : 0),
  );

  const annotationsByIndex = orderedAnnotations.reduce<
    ResolvedCUEAnnotation[][]
  >((byIndex, annotation) => {
    const maxIndex = annotation.index + annotation.length;
    for (let i = annotation.index; i < maxIndex; i += 1) {
      let array = byIndex[i];
      if (!array) {
        array = byIndex[i] = [];
      }
      array.push(annotation);
    }
    return byIndex;
  }, []);

  const textRangesWithAnnotations = annotationsByIndex.reduce<
    {
      start: number;
      end: number;
      annotations: ResolvedCUEAnnotation[];
    }[]
  >((ranges, annotations, index) => {
    const lastRange = ranges[ranges.length - 1];
    if (lastRange?.end === index) {
      // last range ended at current index
      if (isEqualArray(lastRange.annotations, annotations)) {
        // extend last range by one char
        lastRange.end += 1;
      } else {
        // start new range with different annotations
        ranges.push({
          start: index,
          end: index + 1,
          annotations,
        });
      }
    } else {
      if (lastRange) {
        // add text from last range until this index
        ranges.push({
          start: lastRange.end,
          end: index,
          annotations: [],
        });
      } else if (index > 0) {
        // add text from begining until first annotation index
        ranges.push({
          start: 0,
          end: index,
          annotations: [],
        });
      }
      // start new range with annotations
      ranges.push({
        start: index,
        end: index + 1,
        annotations,
      });
    }
    if (
      index === annotationsByIndex.length - 1 &&
      index + 1 < characterArray.length
    ) {
      // text from last annotation until end of plain text
      ranges.push({
        start: index + 1,
        end: characterArray.length,
        annotations: [],
      });
    }
    return ranges;
  }, []);

  return (
    <>
      {textRangesWithAnnotations.map((range, rangeIndex) => {
        const text = characterArray.slice(range.start, range.end).join("");

        if (range.annotations.length) {
          return (
            <AnnotationWrapper annotations={range.annotations} key={rangeIndex}>
              {breakAndReplace(text)}
            </AnnotationWrapper>
          );
        }
        return breakAndReplace(text);
      })}
    </>
  );
}
